import { DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AdvanceReturnType, AdvanceType, DocumentType, ModalDialogType } from 'src/app/@core/constants';
import { PosConfirmationService } from 'src/app/@core/services/pos-confirmation.service';
import { PosOrderObservationsService } from 'src/app/@core/services/pos-order-observations.service';
import { PosPrintDocumentsModalService } from 'src/app/@core/services/pos-print-documents-modal.service';
import { DateUtils } from 'src/app/@core/utils/date-utils';
import { Advance } from 'src/app/models/advance';
import { ColumnTable } from 'src/app/models/column-table';
import { Customer } from 'src/app/models/customer';
import { Filter } from 'src/app/models/filter';
import { AdvanceFilter } from 'src/app/models/filters/advance-filter';
import { Paging } from 'src/app/models/page';
import { Payment } from 'src/app/models/payment';
import { PaymentMethodType } from 'src/app/models/payment-method-type';
import { AdvanceCreateRequest } from 'src/app/models/request/advance-create-request';
import { AdvanceService } from 'src/app/services/advance.service';
import { ConfigurationService } from 'src/app/services/configuration.service';
import { CustomerService } from 'src/app/services/customer.service';
import { ExceptionSolutionService } from 'src/app/services/exception-solution.service';
import { PaymentTypesService } from 'src/app/services/payment-types.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-customer-advances',
  templateUrl: './customer-advances.component.html',
  styleUrls: ['./customer-advances.component.sass']
})
export class CustomerAdvancesComponent implements OnInit {
  private customerCode = '';
  private filter: Filter<AdvanceFilter> = { page: 0, pageSize: 10, value: { isReturn: false } };
  private paymentMethods: Array<PaymentMethodType> = [];
  customer: Customer;
  totalAmount: number;

  advances: Paging<Array<Advance>> = new Paging([]);

  constructor(private advanceService: AdvanceService, private route: ActivatedRoute, private datePipe: DatePipe, private customerService: CustomerService, private configurationService: ConfigurationService, private confirmationService: PosConfirmationService, private router: Router, private readonly posPrintModalService: PosPrintDocumentsModalService, private readonly exceptionSolutionService: ExceptionSolutionService, private readonly observationService: PosOrderObservationsService, private readonly paymentTypeService: PaymentTypesService) {
    this.customer = new Customer();
    this.totalAmount = 0;
  }

  async ngOnInit(): Promise<void> {
    const code = this.route.snapshot.params.id;
    this.customerCode = code;
    this.filter.value.customerCode = code;
    this.paymentMethods = (await this.paymentTypeService.getPaymentMethods(this.configurationService.readSelectedBranchAndSalesOrg().salesOrg.code).toPromise()).response.content;
    this.getAdvances();
    this.getCustomerInfo();
  }

  async getAdvances(): Promise<void> {
    this.advances = (await this.advanceService.readAdvances(this.filter).toPromise()).response;
    this.advances.content.forEach(advance => {
      const date = new Date(advance.timeStamp);
      advance.dateString = this.datePipe.transform(date, 'dd/MM/yyyy') ?? "";
      if (advance.returnAdvanceCode && advance.returnType == null) {
        advance.returnType = AdvanceReturnType.ADVANCE_REFUND;
      }
      if (advance.returnAdvanceCode == undefined) {
        advance.returnType = "";
      }
    });
  }

  getAdvanceHeaders(): Array<ColumnTable> {
    return [
      new ColumnTable('receiptCode', 'Código'),
      new ColumnTable('dateString', 'Fecha'),
      new ColumnTable('advanceType', 'Tipo', { type: "badge", valueMap: this.getAdvanceType() }),
      new ColumnTable('totalAmount', 'Total', { type: "currency" }),
      new ColumnTable('returnType', 'Estado', { type: "badge", valueMap: this.getReturnType() }),
      new ColumnTable('code', 'Opciones', {
        type: 'action',
        actions: [
          {
            fn: advance => this.printAdvance(advance),
            icon: 'fa fa-print',
            tooltip: 'Imprimir recibo',
            isDisabled: false,
          },
          {
            fn: advance => this.requestRefund(advance, AdvanceReturnType.ADVANCE_REFUND),
            icon: 'fa fa-undo',
            tooltip: 'Devolver',
            isDisabled: false,
            isPrivileged: true,
            privilege: 'returnAdvance',
            disabledFn: (advance: Advance) => advance.returnAdvanceCode || advance.totalAmount <= 0 || this.validateDate(advance) ? true : false
          },
          {
            fn: advance => this.requestRefund(advance, AdvanceReturnType.ADVANCE_REQUEST_REFUND),
            icon: 'fa fa-money-bill',
            tooltip: 'Solicitud de cheque',
            isDisabled: false,
            isPrivileged: true,
            privilege: 'returnAdvance',
            disabledFn: (advance: Advance) => advance.returnAdvanceCode || advance.totalAmount <= 0 ? true : false
          }
        ]
      }),
    ];
  }

  async printAdvance(advance: Advance) {
    await this.posPrintModalService.showPrintAdvanceDocuments(advance);
  }

  async requestRefund(advance: Advance, documentType: AdvanceReturnType) {
    await this.confirmationService.show("Solicitud de cheque", `¿Esta seguro que desea continuar con la ${documentType == AdvanceReturnType.ADVANCE_REFUND ? "devolucion" : "solicitud de cheque"}?`, ModalDialogType.WARNING, async () => {
      if (advance.salesOrg == this.configurationService.readSelectedBranchAndSalesOrg().salesOrg.code) {
        const data = documentType == AdvanceReturnType.ADVANCE_REFUND ? await this.createAdvanceRefund(advance) : await this.createAdvanceRequestRefund(advance);
        Swal.fire({
          title: `${documentType == AdvanceReturnType.ADVANCE_REFUND ? 'Devolución de Anticipo' : 'Solicitud de cheque'}`,
          html: `Operacion Exitosa </br> ${documentType == AdvanceReturnType.ADVANCE_REFUND ? 'Se hizo la devolución del anticipo' : 'Se hizo la solicitud de cheque'}`,
          icon: 'success',
          confirmButtonText: 'Aceptar',
        });
        this.getAdvances();
        advance.returnAdvanceCode = data.code;
        await this.printAdvance(advance);
      }
      else {
        this.exceptionSolutionService.showAlertWithSolution(`${documentType == AdvanceReturnType.ADVANCE_REFUND ? 'Devolución de Anticipo' : 'Solicitud de cheque'}`, `${documentType == AdvanceReturnType.ADVANCE_REFUND ? 'No se puede hacer la devolución en otra sociedad' : 'No se puede hacer la solicitud de cheque en otra sociedad'}`);
      }
    });
  }
  async createAdvanceRequestRefund(advance: Advance) {
    return (await this.advanceService.requestRefund(advance, AdvanceReturnType.ADVANCE_REQUEST_REFUND, AdvanceType.SHOP).toPromise()).response;
  }

  private async createAdvanceRefund(advance: Advance) {
    const request = new AdvanceCreateRequest();
    request.paymentsRequestModel.branchCode = this.configurationService.readSelectedBranchAndSalesOrg().branch.code;
    request.paymentsRequestModel.salesOrgCode = this.configurationService.readSelectedBranchAndSalesOrg().salesOrg.code;
    request.paymentsRequestModel.documentType = AdvanceReturnType.ADVANCE_REFUND;
    request.paymentsRequestModel.payments = advance.paymentDetail.map(s => {
      let payment = new Payment();
      Object.assign(payment, s);
      payment.amount = payment.amount * -1;
      payment.documentType = DocumentType.ADVANCE_REFUND;
      payment.receiptNumber = advance.receiptCode;
      payment.currency = this.paymentMethods.find(x => x.code == s.paymentMethodCode)?.currency ?? "HNL"
      return payment;
    });
    request.replicateInExternalSystem = true;
    request.customerCode = this.customer.code;
    request.observations = await this.observationService.showUnlockOrderObservations(request.observations) ?? "";
    request.oldAdvanceCode = advance.code;
    request.advanceType = AdvanceType.SHOP;
    request.originalAdvanceCode = advance.code;
    request.origin = "POS";

    const data = (await this.advanceService.createAdvance(request).toPromise()).response;
    return data;
  }

  validateDate(advance: Advance): boolean {
    return new Date() > DateUtils.getDateFromStringForReports(advance.timeStamp)
  }

  getAdvancesByPage(event: Filter<AdvanceFilter>): void {
    this.filter = event;
    this.filter.value.isReturn = false;
    this.filter.value.customerCode = this.customerCode;
    this.getAdvances();
  }

  private async getCustomerInfo(): Promise<void> {

    this.customer = (await this.customerService.readCustomerByCode(this.customerCode).toPromise());
    const response = (await this.advanceService.readAdvancesBalance(this.customerCode).toPromise()).response;
    this.totalAmount = response.totalAmount;
  }


  private getReturnType(): Map<string, string> {
    let result = new Map<string, string>();
    result.set("", "Activo");
    result.set("ADVANCE_REFUND", "Devuelto");
    result.set("ADVANCE_REQUEST_REFUND", "Solicitud de cheque realizada");

    return result;
  }

  createAdvance() {
    this.router.navigate(["customer", "advances", this.customer.code, "create"]);
  }

  getAdvanceType(): Map<string, string> | undefined {
    let result = new Map<string, string>();
    result.set("", "Activo");
    result.set("WEDDING", "Boda");
    result.set("SHOP", "Tienda");

    return result;
  }
}
